import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = [
    "All",
    "Business Development",
    "Nonprofit Strategy",
    "Real Estate",
    "Grant Writing",
    "Community Development",
    "Leadership",
];

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [cat, setCat] = useState("All");

    useEffect(() => {
        api.get("/blog").then(({ data }) => setPosts(data));
    }, []);

    const filtered = cat === "All" ? posts : posts.filter((p) => p.category === cat);

    return (
        <div data-testid="blog-page">
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Insights</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        Business & Community{" "}
                        <span className="italic text-[#D4AF37]">Insights.</span>
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">
                        Field notes on building businesses, nonprofits, and
                        community programs in Knoxville and beyond.
                    </p>
                </div>
            </section>

            <section className="border-b border-slate-200 sticky top-20 bg-white z-10">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex gap-2 overflow-x-auto py-4">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`whitespace-nowrap px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${
                                cat === c
                                    ? "bg-[#0A192F] text-white"
                                    : "text-slate-600 hover:text-[#0A192F]"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    {filtered.length === 0 ? (
                        <p className="text-center text-slate-500">No posts in this category yet.</p>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filtered.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`blog-post-${p.slug}`}
                                    className="group exec-card overflow-hidden flex flex-col"
                                >
                                    {p.cover_image && (
                                        <div className="aspect-[4/3] overflow-hidden bg-slate-100 flex items-center justify-center">
                                            <img
                                                src={p.cover_image}
                                                alt={p.title}
                                                loading="lazy"
                                                className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700"
                                            />
                                        </div>
                                    )}
                                    <div className="p-7 flex flex-col flex-1">
                                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                                            {p.category}
                                        </span>
                                        <h2 className="font-serif text-xl font-bold mt-3 text-[#0A192F] leading-snug">
                                            {p.title}
                                        </h2>
                                        <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                                            {p.excerpt}
                                        </p>
                                        <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                                            <span className="flex items-center gap-1.5">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {new Date(p.published_at).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-[#0A192F] group-hover:text-[#D4AF37] transition-colors" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
